import { httpApi } from '@api/http.api.ts';
import type { LEARNING_PATH_TYPES, LEARNING_STATES } from '@constants/learningStates.ts';
import type { CommonSearchRequest, PaginatedResponse } from '@types';

interface LearningCategory {
    name: string;
    focus: string;
}

export interface LearningDetails {
    category: LearningCategory;
    title: string;
    shortDescription: string;
    description: string;
    creator: string;
    updatedOnUTC: string;
    learningObjectives: LearningObjective[];
}

export interface LearningObjective {
    name: string;
}

export interface ProgressInfo {
    totalTimeInMinutes: number;
    spendTimeInMinutes: number;
    progressPercent: number;
}

export interface LearningUserActivity {
    progress: ProgressInfo;
    states: LearningState[];
    assignedAtUTC: string | null;
    completedAtUTC: string | null;
    learningUserType: LearningPathType;
    dueDateUTC: string | null;
    dueNoticeUTC: string | null;
}

export interface LearningResponse {
    id: number;
    learningDetails: LearningDetails;
    userActivity: LearningUserActivity | null;
    thumbNailURI: string;
    rankIds: number[];
}

export interface LearningRankDescription {
    id: number;
    name: string;
}

export interface LearningResultsDescription {
    allPathwaysCompleted: boolean;
    overdue: number;
    ranks: LearningRankDescription[];
    lastPathwayIdUsed: number | null;
    lastElementIdCompleted: number | null;
}

export const SCORE_TYPES = {
    Bonus: 'Bonus',
    Standard: 'Standard',
} as const;

export type LearningState = (typeof LEARNING_STATES)[keyof typeof LEARNING_STATES];
export type LearningPathType = (typeof LEARNING_PATH_TYPES)[keyof typeof LEARNING_PATH_TYPES];
export type ScoreType = (typeof SCORE_TYPES)[keyof typeof SCORE_TYPES];

export interface RankPoint {
    value: number;
    scoreType: ScoreType;
    gameAction: string;
    eventDateTimeUTC: string;
    code: string;
}

export interface UserScoreResponse {
    unseenScores: RankPoint[];
    score: number;
}

export const getApiLearning = (
    learningPathType: LearningPathType,
    payload: CommonSearchRequest,
): Promise<PaginatedResponse<LearningResponse, LearningResultsDescription>> =>
    httpApi
        .post<
            PaginatedResponse<LearningResponse, LearningResultsDescription>
        >(`learning/${learningPathType}/search`, { ...payload })
        .then(({ data }) => data);

export const putApiToggleLearningBookmark = (id: number): Promise<any> =>
    httpApi.put<any>(`learning/bookmark-toggle/${id}`).then(({ data }) => data);

export const getApiLearningBookmarks = (): Promise<number[]> =>
    httpApi.get<number[]>('learning/bookmarks').then(({ data }) => data);

export const getApiPoints = (): Promise<UserScoreResponse> =>
    httpApi.get<UserScoreResponse>('me/points').then(({ data }) => data);

export const deleteApiMarkPointAsSeen = (date: string): Promise<any> =>
    httpApi.delete<any>(`me/points/seen/${date}`).then(({ data }) => data);
